// PO7-S5 (docs/build-briefs/PO7-S5-invoice-scan-receive.md; DEC-411). The rules of the invoice-scan receive screen,
// with no DOM and no network of its own, so test/ui-receive-scan-model.test.ts proves each one directly.
//
// WHAT THE SCREEN GUARANTEES, and where each is decided here:
//   - Nothing is received because an invoice said so. A line starts Pending with the quantity the invoice read; only a
//     tap makes it Accepted, linked or Not received (acceptLine, linkLine, markNotReceived). The invoice is never
//     posted as received truth.
//   - Finish receive is available only when every line is handled (canFinish), and a delivery with no order also needs
//     its vendor confirmed and must not repeat an invoice already recorded.
//   - What posts is what the person accepted: runFinish posts each Accepted line ONCE through the existing receipt
//     command, with a stable idempotency key per line, so a repeated Finish replays instead of posting twice.
//   - A discrepancy is a quiet mark (lineMarks), never a sheet and never a block. The server's own refusal (an
//     over-receipt) is shown on the line and the line stays editable.
//   - Quantities are exact decimal strings (scaled integers), never floats.

import { normalizeLeadingDot } from "../../src/domain/leadingDot.ts";
import type {
  ReceiveScanDraftDto,
  ReceiveScanOrderLineTargetDto,
  ReceiveScanTargetDto,
} from "./api.ts";
import {
  CONTAINER_RECEIVING_UNIT_VALUE,
  describeOrderUnitQuantity,
  receivingUnitOptionsForItemKind,
  type ReceivingProductContainerInfo,
} from "./purchasingDisplay.ts";

export const NO_VALUE = "-";

const SCALE = 10_000n;
const MAX_SCALED = 999_999n * SCALE;
const QUANTITY_PATTERN = /^\d{1,6}(\.\d{1,4})?$/;

/** A decimal string as ten-thousandths, or null when it is not a non-negative decimal of at most four places. */
export function parseQuantity(text: string): bigint | null {
  const trimmed = normalizeLeadingDot(text);
  if (!QUANTITY_PATTERN.test(trimmed)) {
    return null;
  }
  const [whole = "0", fraction = ""] = trimmed.split(".");
  return BigInt(whole) * SCALE + BigInt(fraction.padEnd(4, "0"));
}

/** `12000` ten-thousandths is "1.2"; `20000` is "2". */
export function formatQuantity(scaled: bigint): string {
  const whole = scaled / SCALE;
  const fraction = (scaled % SCALE).toString().padStart(4, "0").replace(/0+$/, "");
  return fraction.length === 0 ? whole.toString() : `${whole}.${fraction}`;
}

/** The quantity one step away, never below zero and never above the largest the receipt command accepts. */
export function stepQuantity(text: string, direction: 1 | -1): string {
  const current = parseQuantity(text) ?? 0n;
  const next = current + BigInt(direction) * SCALE;
  if (next < 0n) {
    return "0";
  }
  return formatQuantity(next > MAX_SCALED ? MAX_SCALED : next);
}

function isPositive(text: string): boolean {
  const scaled = parseQuantity(text);
  return scaled !== null && scaled > 0n;
}

export type LineStatus = "pending" | "accepted" | "not_received";

export interface ScanLine {
  lineNumber: number;
  /** The words printed on the invoice. */
  description: string;
  vendorItemCode: string | null;
  /** What the invoice read, or null when it read none. */
  invoiceQuantity: string | null;
  /** What the person will record. Starts as the invoice quantity; the steppers move it. */
  quantity: string;
  lowConfidence: boolean;
  /** What the line is received as: a matched order line or item, or one the person linked. */
  target: ReceiveScanTargetDto | null;
  /** A weaker pairing, offered pre-selected in the link list and never applied. */
  candidate: ReceiveScanTargetDto | null;
  linked: boolean;
  status: LineStatus;
  /** Barcode ticks so far, in the line's own unit (the hook B6 calls). */
  count: string | null;
  idempotencyKey: string | null;
  keyBasis: string | null;
  posted: boolean;
  error: string | null;
}

/** Every product line of a read invoice, Pending. */
export function initLines(draft: ReceiveScanDraftDto): ScanLine[] {
  return draft.lines.map((line) => ({
    lineNumber: line.lineNumber,
    description: line.description?.trim() || line.vendorItemCode?.trim() || NO_VALUE,
    vendorItemCode: line.vendorItemCode,
    invoiceQuantity: line.quantity,
    quantity: line.quantity !== null && parseQuantity(line.quantity) !== null ? line.quantity : "0",
    lowConfidence: line.lowConfidence,
    target: line.target,
    candidate: line.candidate,
    linked: false,
    status: "pending" as const,
    count: null,
    idempotencyKey: null,
    keyBasis: null,
    posted: false,
    error: null,
  }));
}

/** The quantity is zero: the tap that would be Accept is Not received instead (nothing arrived on this line). */
export function isZeroQuantity(line: ScanLine): boolean {
  return parseQuantity(line.quantity) === 0n;
}

export function canAccept(line: ScanLine): boolean {
  return !line.posted && line.target !== null && isPositive(line.quantity);
}

/** Accept needs a target and a quantity above zero; anything else is left as it is. */
export function acceptLine(line: ScanLine): ScanLine {
  if (!canAccept(line)) {
    return line;
  }
  return { ...line, status: "accepted", error: null };
}

/** Back to Pending (tapping Accepted again). A line already posted stays. */
export function undoLine(line: ScanLine): ScanLine {
  if (line.posted) {
    return line;
  }
  return { ...line, status: "pending", error: null };
}

export function markNotReceived(line: ScanLine): ScanLine {
  if (line.posted) {
    return line;
  }
  return { ...line, status: "not_received", error: null };
}

export function setLineQuantity(line: ScanLine, quantity: string): ScanLine {
  if (line.posted || parseQuantity(quantity) === null) {
    return line;
  }
  return { ...line, quantity, error: null };
}

export function stepLine(line: ScanLine, direction: 1 | -1): ScanLine {
  return setLineQuantity(line, stepQuantity(line.quantity, direction));
}

/** Link and add: the person picked an item for an unmatched (or wrong) line. Accepted at once when there is a quantity. */
export function linkLine(line: ScanLine, target: ReceiveScanTargetDto): ScanLine {
  if (line.posted) {
    return line;
  }
  return {
    ...line,
    target,
    linked: true,
    status: isPositive(line.quantity) ? "accepted" : "pending",
    error: null,
  };
}

export function handledCount(lines: readonly ScanLine[]): number {
  return lines.filter((line) => line.status !== "pending").length;
}

export interface FinishContext {
  /** The purchase order the invoice is attached to, or null for a delivery with no order. */
  orderId: string | null;
  orderVendorId: string | null;
  /** No order: the vendor the person confirmed by a tap. */
  vendorId: string | null;
  invoiceNumber: string | null;
  /** No order: the source reference ONE receipt carries so the same invoice cannot be recorded twice. */
  receiveReference: string | null;
  /** No order: this vendor and number were already recorded. */
  duplicateRecorded: boolean;
}

/** The reason Finish is not available, or null when it is. Terse by design: it names the missing thing. */
export function finishBlocker(lines: readonly ScanLine[], context: FinishContext): string | null {
  if (lines.length === 0) {
    return "No lines";
  }
  if (context.orderId === null && context.duplicateRecorded) {
    return "Already recorded";
  }
  if (context.orderId === null && context.vendorId === null) {
    return "Confirm vendor";
  }
  if (handledCount(lines) < lines.length) {
    return "Lines left";
  }
  return null;
}

export function canFinish(lines: readonly ScanLine[], context: FinishContext): boolean {
  return finishBlocker(lines, context) === null;
}

export type LineMark = "check_quantity" | "differs_from_order" | "not_on_order";

export const MARK_TEXT: Readonly<Record<LineMark, string>> = {
  check_quantity: "Check quantity",
  differs_from_order: "Differs from order",
  not_on_order: "Not on this order",
};

/** The quiet amber marks on a line. A mark never blocks anything. */
export function lineMarks(line: ScanLine, context: { hasOrder: boolean }): LineMark[] {
  const marks: LineMark[] = [];
  if (line.lowConfidence && line.status === "pending") {
    marks.push("check_quantity");
  }
  const target = line.target;
  if (target !== null && context.hasOrder) {
    if (target.type === "item") {
      marks.push("not_on_order");
    } else {
      const wanted = parseQuantity(target.outstandingQuantity);
      const entered = parseQuantity(line.quantity);
      if (wanted !== null && entered !== null && wanted !== entered) {
        marks.push("differs_from_order");
      }
    }
  }
  return marks;
}

function bottlesPerUnit(target: ReceiveScanTargetDto): string | null {
  const pack = target.bottlesPerOrderUnit;
  return pack !== null && Number(pack) > 1 ? pack : null;
}

/** "2 cases (24 bottles)" for a line received by the case, the plain number otherwise. */
export function describeLineQuantity(line: ScanLine): string {
  const target = line.target;
  if (target === null) {
    return line.quantity;
  }
  return describeOrderUnitQuantity(
    { orderUnit: target.orderUnit, bottlesPerOrderUnit: target.bottlesPerOrderUnit },
    line.quantity,
  );
}

/** What the screen shows under the quantity when the line is counted in cases: both readings, else empty. */
export function packedReading(line: ScanLine): string {
  const target = line.target;
  if (target === null || bottlesPerUnit(target) === null) {
    return "";
  }
  const reading = describeLineQuantity(line);
  return reading === line.quantity ? "" : reading;
}

export interface ProductReceiveInfo {
  unitPriceCents: number | null;
  containerType: string | null;
  sizeValue: string | null;
  sizeUnit: string | null;
}

function containerOf(info: ProductReceiveInfo): ReceivingProductContainerInfo {
  return {
    containerType: info.containerType,
    sizeValue: info.sizeValue,
    sizeUnit: info.sizeUnit,
  };
}

export type ReceiptBodyResult =
  { ok: true; body: Record<string, string | number> } | { ok: false; error: string };

/** The request body for one accepted line, in the units the receipt command takes. The unit is the item's own count
 * unit, never typed: a case line goes in cases, a bottle or can with a known size as a container count, a packaged item
 * in units, anything else in bottles. */
export function buildReceiptBody(
  line: ScanLine,
  context: FinishContext,
  idempotencyKey: string,
  products: ReadonlyMap<string, ProductReceiveInfo>,
): ReceiptBodyResult {
  const target = line.target;
  if (target === null) {
    return { ok: false, error: "Link to an item" };
  }
  if (!isPositive(line.quantity)) {
    return { ok: false, error: "Enter a quantity" };
  }
  const body: Record<string, string | number> = {
    idempotencyKey,
    liquorProductId: target.liquorProductId,
  };

  const orderLineTarget: ReceiveScanOrderLineTargetDto | null =
    target.type === "order_line" ? target : null;
  if (orderLineTarget !== null && bottlesPerUnit(orderLineTarget) !== null) {
    body["receivedOrderUnits"] = line.quantity;
  } else {
    let quantity = line.quantity;
    const pack = target.type === "item" ? bottlesPerUnit(target) : null;
    if (pack !== null) {
      const scaled = (parseQuantity(line.quantity) ?? 0n) * (parseQuantity(pack) ?? 0n);
      if (scaled % (SCALE * SCALE) !== 0n) {
        return { ok: false, error: "Not a whole number of bottles" };
      }
      quantity = (scaled / (SCALE * SCALE)).toString();
    }
    const info = products.get(target.liquorProductId);
    if (info === undefined) {
      return { ok: false, error: "Could not load this item" };
    }
    const options = receivingUnitOptionsForItemKind(info.unitPriceCents, containerOf(info));
    if (options.some((option) => option.value === CONTAINER_RECEIVING_UNIT_VALUE)) {
      body["receivedContainerCount"] = quantity;
    } else if (options.length === 1 && options[0] !== undefined) {
      body["quantity"] = quantity;
      body["unit"] = options[0].value;
    } else {
      body["quantity"] = quantity;
      body["unit"] = "bottle";
    }
  }

  if (context.orderId !== null) {
    if (orderLineTarget !== null) {
      body["purchaseOrderLineId"] = orderLineTarget.orderLineId;
    } else {
      if (context.orderVendorId !== null) {
        body["vendorId"] = context.orderVendorId;
      }
      body["note"] = "Not on this order";
    }
  } else {
    body["source"] = "outside_system";
    if (context.vendorId !== null) {
      body["vendorId"] = context.vendorId;
    }
    if (context.invoiceNumber !== null) {
      body["note"] = `Invoice ${context.invoiceNumber}`.slice(0, 200);
    }
  }
  return { ok: true, body };
}

export interface PostResult {
  /** 200 or 201 is a receipt (a 200 is a replay). Anything else is a refusal or a failure to reach the server. */
  status: number;
  message: string | null;
}

export interface FinishOutcome {
  lines: ScanLine[];
  /** Every accepted line is now posted. */
  complete: boolean;
  /** The invoice was already recorded: the first receipt was refused on its reference and nothing else was sent. */
  duplicate: boolean;
}

const DUPLICATE_PATTERN = /already recorded/i;

/**
 * Posts every Accepted, not-yet-posted line, in order, one at a time. Each line keeps its idempotency key for the visit
 * (replaced only when its target or quantity changes), so tapping Finish again after a failure replays the lines that
 * went through instead of posting them twice. For a delivery with no order ONE receipt carries the invoice's source
 * reference; the server's unique index refuses a second recording of the same invoice on that write, so a repeat is
 * stopped before it posts anything.
 */
export async function runFinish(params: {
  lines: readonly ScanLine[];
  context: FinishContext;
  products: ReadonlyMap<string, ProductReceiveInfo>;
  mintKey: () => string;
  post: (body: Record<string, string | number>) => Promise<PostResult>;
  onLine?: (line: ScanLine) => void;
}): Promise<FinishOutcome> {
  const lines = params.lines.map((line) => ({ ...line }));
  let markerNeeded = params.context.orderId === null && params.context.receiveReference !== null;
  // A marker already carried by a posted line of this visit counts as placed.
  if (lines.some((line) => line.posted)) {
    markerNeeded = false;
  }
  for (const line of lines) {
    if (line.status !== "accepted" || line.posted) {
      continue;
    }
    const basis = `${line.target?.liquorProductId ?? ""}|${line.target?.type === "order_line" ? line.target.orderLineId : ""}|${line.quantity}`;
    if (line.idempotencyKey === null || line.keyBasis !== basis) {
      line.idempotencyKey = params.mintKey();
      line.keyBasis = basis;
    }
    const built = buildReceiptBody(line, params.context, line.idempotencyKey, params.products);
    if (!built.ok) {
      line.error = built.error;
      params.onLine?.(line);
      continue;
    }
    const carriesMarker = markerNeeded;
    if (carriesMarker && params.context.receiveReference !== null) {
      built.body["sourceReference"] = params.context.receiveReference;
    }
    const result = await params.post(built.body);
    if (result.status === 200 || result.status === 201) {
      line.posted = true;
      line.error = null;
      if (carriesMarker) {
        markerNeeded = false;
      }
      params.onLine?.(line);
      continue;
    }
    if (carriesMarker && DUPLICATE_PATTERN.test(result.message ?? "")) {
      line.error = null;
      params.onLine?.(line);
      return { lines, complete: false, duplicate: true };
    }
    line.error = result.message ?? "Could not record this line";
    params.onLine?.(line);
  }
  const complete = lines.every((line) => line.status !== "accepted" || line.posted);
  return { lines, complete, duplicate: false };
}

export interface FinishSummary {
  full: number;
  short: number;
  notReceived: number;
}

/** The done screen's three figures. "Short" is an order line received below what was outstanding. */
export function summarize(lines: readonly ScanLine[]): FinishSummary {
  let full = 0;
  let short = 0;
  let notReceived = 0;
  for (const line of lines) {
    if (line.status === "not_received") {
      notReceived += 1;
    } else if (line.status === "accepted") {
      const target = line.target;
      const wanted =
        target !== null && target.type === "order_line"
          ? parseQuantity(target.outstandingQuantity)
          : null;
      const entered = parseQuantity(line.quantity);
      if (wanted !== null && entered !== null && entered < wanted) {
        short += 1;
      } else {
        full += 1;
      }
    }
  }
  return { full, short, notReceived };
}

/**
 * The barcode hook (B6, feat/b6-barcode-receive, not merged when this was built). A scan of an item ticks the matching
 * lines toward what the invoice says: the count moves by `delta` bottles (a case scan passes the case size), shown in
 * the line's own unit, and the line is accepted when the count EQUALS the invoice quantity. A line that is Not received
 * or already posted is left alone, a count never goes below zero, and a count past the invoice quantity is kept (so the
 * person sees the difference) without accepting. Returns the lines unchanged when nothing matches.
 */
export function countScannedUnit(
  lines: readonly ScanLine[],
  liquorProductId: string,
  delta: number,
): ScanLine[] {
  return lines.map((line) => {
    const target = line.target;
    if (
      target === null ||
      target.liquorProductId !== liquorProductId ||
      line.posted ||
      line.status === "not_received"
    ) {
      return line;
    }
    const perUnit = Number(bottlesPerUnit(target) ?? "1");
    const current = parseQuantity(line.count ?? "0") ?? 0n;
    const moved = current + BigInt(Math.round((delta / perUnit) * Number(SCALE)));
    const next = moved < 0n ? 0n : moved;
    const count = formatQuantity(next);
    const invoice = line.invoiceQuantity === null ? null : parseQuantity(line.invoiceQuantity);
    const reached = invoice !== null && next === invoice && invoice > 0n;
    if (reached) {
      return { ...line, count, quantity: count, status: "accepted" as const, error: null };
    }
    // A line the count had accepted goes back to Pending when the count moves off the invoice quantity; a line the person
    // accepted by hand keeps that.
    const wasReached = invoice !== null && current === invoice && invoice > 0n;
    return {
      ...line,
      count,
      quantity: next > 0n ? count : line.quantity,
      status: line.status === "accepted" && wasReached ? ("pending" as const) : line.status,
    };
  });
}
